import { Flex, Menu, Button, Select, Stack } from '@mantine/core';
import { IconSettings } from '@tabler/icons-react';
import { useState } from 'react';
import MarkdownEditor from 'src/common/components/markdown-editor/MarkdownEditor';
import { GroupedSuggestions } from 'src/common/components/markdown-editor/plugins/SuggestionTrackerPlugin';

const content = `Example document for testing the editor. Contains a variety of markdown and HTML the editor should support, as well as some manual tests at the end (e.g. pasting markdown).
# MD Elements

## Images

A base64 data-URI image (must render as an image, not an about:blank hyperlink):

![Image](data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAPAAAACMCAIAAADN17N/AAABWklEQVR42u3SMRHAIBREwa+ELgUaIocy0iMitJHAwd6sgptXrd+wjXIBggZBg6BB0Aj653sKIggaQYOgQdAgaAQtaAQNggZBg6ARtKARNAgaBA2CRtCCRtAgaBA0CBpBCxpBg6BB0CBoBC1oBA2CBkGDoBE0CBoEDYIGQSNoEDQIGgSNoAWNoEHQsEbQZiETtAnaTNBmgjYTtAla0CZoM0GbCdpM0CZoQZugzQRtJmgzQZugBW2CNhO0maDNBG2CFrQJ2kzQZoI2E7QJWtAmaDNBmwnaTNAmaDNBmwnaTNBmgjZBmwnaTNBmgjZBC9oEbSZos2WCvsYLEQSNoEHQIGgQNIIWNIIGQYOgQdAIWtAIGgQNggZBI2hBI2gQNAgaBI2gBY2gQdAgaBA0ghY0ggZBg6BB0AgaBA2CBkGDoBE0CBoEDYJG0IJG0JAbNOQSNIIGQYOgQdAcbALwbZ5Whk9F/AAAAABJRU5ErkJggg==)

An http image (the placeholder service draws the label on the image itself, so a visible "HTTP image loaded over network" picture means it rendered as an image, not a hyperlink):

![Advisor Signature](https://placehold.co/400x120/2563eb/ffffff/png?text=HTTP+image+loaded+over+network)

## Table with suggestions

| Name | Age | Location | Favorite Color |
| ---- | --- | -------- | -------------- |
| Bob  | 30  | New York | Blue           |
| Alice| 25 | <suggestion>New York</suggestion> | Red            |
| Jane | 35 | New <suggestion source="note">added</suggestion> York | Green          |

## Suggestion with table

<suggestion source="note">
| Name | Age | Location | Favorite Color |
| ---- | --- | -------- | -------------- |
| Bob  | 30  | New York | Blue           |
| Alice| 25 | New York | Red            |
| Jane | 35 | New added York | Green          |
</suggestion>


## Bug hunting

### Assets
| Description | Current value* |
| :---- | :---- |
| Aviva Stocks and Shares ISA held jointly with spouse, originally opened in 2008 and consolidated from three previous providers including Halifax, Standard Life, and a legacy Equitable Life policy that was transferred during the demutualisation process | £{ADVISOR_TO_COMPLETE} |
| {ASSET NAME} | £125,000.00 representing the current surrender value as of the most recent quarterly statement received in March, net of any outstanding charges and adjustments for pending dividend reinvestments |
| {ASSET NAME} | £{ADVISOR_TO_COMPLETE} |

### Liabilities
| Description | Remaining balance* |
| :---- | :---- |
| {LIABILITY NAME} | £{ADVISOR_TO_COMPLETE} |
| {LIABILITY NAME} | £{ADVISOR_TO_COMPLETE} |



## Text with suggestions from two sources
Here's inline text with <suggestion>default suggestion</suggestion> and <suggestion source="note">note suggestion</suggestion>.

## Code block

\`\`\`js
function helloWorld() {
  console.log('Hello, world!');
}
\`\`\`

## Suggestion block
<suggestion>
Here's a default suggestion block with a link: [Google](https://www.google.com)

And a list:

- Item 1
- Item 2
- Item 3
</suggestion>

# HTML
## HTML tag with a mix of HTML child elements
<html>
<h1>h1</h1>
<suggestion source="note">
Suggestion from a note
</suggestion>
<br/>
And an <suggestion source="note">inline note suggestion</suggestion>.
<p>
And a <suggestion>non-note suggestion</suggestion>.
</p>
<table>
  <tr>
    <td>Cell 1</td>
    <td>Cell 2</td>
  </tr>
</table>
</html>

## Top-level BR tag
<br/>

## Top-level Table tag (multiline + suggestion)
<table>
  <tr>
    <td>Cell 1</td>
    <td><suggestion>Cell 2</suggestion></td>
  </tr>
</table>

## Inline Table tag (single line)
<table><tr><td>Inline 1</td><td>Inline 2</td></tr></table>

# Markdown after HTML

## Suggestion block with a list inside
<suggestion source="note">
Here's a note suggestion block:

- Note item 1
- Note item 2
</suggestion>

## Numbered list

1. Item 1
2. Item 2
3. Item 3

## Quote

> This is a quote

## Tooltip attribute
Hover over these: <suggestion tooltip="A helpful tip">suggestion with tooltip</suggestion> and <suggestion source="note" tooltip="Note-sourced tip">note with tooltip</suggestion>.

<suggestion tooltip="Block-level tooltip">
This is a block suggestion with a tooltip. Hover anywhere over this block to see it.

- Item with tooltip parent
- Another item
</suggestion>

## Page breaks inside suggestions

An inline suggestion containing a page break (should render the page break visual inside the suggestion highlight):

<suggestion source="inline">\\newpage</suggestion>

A deletion suggestion containing a page break (accepting removes it, discarding keeps it):

<suggestion source="deletion">\\newpage</suggestion>

An inline suggestion mixing text and a page break:

<suggestion source="inline">
Text before the break
\\newpage
Text after the break
</suggestion>

## Invalid source (should fallback to default)
Testing invalid source (should fallback to default): <suggestion source="invalid">This should be default styled</suggestion>

## Scrolling table

| Name | Age | Location | Favorite Color | Occupation | Company | Country | Phone | Email | Notes |
| ---- | --- | -------- | -------------- | ---------- | ------- | ------- | ----- | ----- | ----- |
| Bob | 30 | New York | Blue | Engineer | Acme Corp | USA | +1 555 0100 | bob@example.com | Likes hiking |
| Alice | 25 | London | Red | Designer | DesignCo | UK | +44 7700 900000 | alice@example.com | Plays piano |
| Jane | 35 | Sydney | Green | Manager | BigCorp | Australia | +61 400 000 000 | jane@example.com | Runs marathons |

## Scrolling table in suggestion

<suggestion source="note">
| Name | Age | Location | Favorite Color | Occupation | Company | Country | Phone | Email | Notes |
| ---- | --- | -------- | -------------- | ---------- | ------- | ------- | ----- | ----- | ----- |
| Bob | 30 | New York | Blue | Engineer | Acme Corp | USA | +1 555 0100 | bob@example.com | Likes hiking |
| Alice | 25 | London | Red | Designer | DesignCo | UK | +44 7700 900000 | alice@example.com | Plays piano |
| Jane | 35 | Sydney | Green | Manager | BigCorp | Australia | +61 400 000 000 | jane@example.com | Runs marathons |
</suggestion>

# Manual tests
## Paste markdown test
Paste this markdown into the editor:

\`\`\`
**bolded text**
\`\`\`




Paste this HTML table into the editor:

\`\`\`
<table>
  <tr>
    <td>Cell 1</td>
    <td>Cell 2</td>
  </tr>
</table>
\`\`\`



`;
export default function EditorPlaygroundTab() {
  const [value, setValue] = useState(content);
  const [suggestions, setSuggestions] = useState<GroupedSuggestions>({
    default: {},
    note: {},
    inline: {},
    placeholder: {},
    deletion: {},
  });
  const [enableInlineEditing, setEnableInlineEditing] = useState(false);

  return (
    <Stack gap="xs">
      <Flex justify="end" gap="md" align="end">
        <Select
          label="Default Suggestions"
          placeholder="Select to scroll"
          data={Object.values(suggestions.default).map(s => ({
            value: s.key,
            label: s.textContent.slice(0, 50) || '(empty)',
          }))}
          onChange={key => key && suggestions.default[key]?.scrollIntoView()}
          clearable
        />
        <Select
          label="Note Suggestions"
          placeholder="Select to scroll"
          data={Object.values(suggestions.note).map(s => ({
            value: s.key,
            label: s.textContent.slice(0, 50) || '(empty)',
          }))}
          onChange={key => key && suggestions.note[key]?.scrollIntoView()}
          clearable
        />
        <Menu shadow="md" width={200}>
          <Menu.Target>
            <Button variant="outline" leftSection={<IconSettings size={16} />}>
              Options
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item onClick={() => setEnableInlineEditing(prev => !prev)}>
              {enableInlineEditing ? 'Disable' : 'Enable'} inline editing
            </Menu.Item>
            <Menu.Item
              onClick={() => {
                // eslint-disable-next-line no-console
                console.log(value);
              }}
            >
              Export to dev console
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Flex>
      <MarkdownEditor
        onChange={setValue}
        defaultValue={content}
        onSuggestionsChange={setSuggestions}
        enableInlineEditing={enableInlineEditing}
      />
    </Stack>
  );
}
